import {
  ERROR_CODES,
  ERROR_DESCRIPTIONS,
  ErrorCodeKey,
  ErrorCodeValue,
  ErrorDescription,
} from '@roll-web/constants';
import axios from 'axios';

/** 3bArc APIが返すエラー */
type ArcApiError = {
  code: string;
  message: string;
};

/** AxiosErrorResponseをエラーをErrorに変換する
 * 基本的にAPIエラー時にcatchされた際に呼び出す
 */
export const convertErrorToErrorDescription = (
  error: unknown,
  defaultTitle: string
): Error => {
  const defaultError = createDefaultError(defaultTitle);
  if (isArcApiError(error)) {
    return convertArcApiErrorToError(error);
  }
  if (!(error instanceof Error)) return defaultError;
  if (!axios.isAxiosError(error)) {
    defaultError.message = error.name;
    return defaultError;
  }
  if (error.response && isArcApiError(error.response?.data)) {
    return convertArcApiErrorToError(error.response.data);
  }
  return defaultError;
};

/** レスポンスBodyがArcApiErrorであることを想定して、Errorに変換する
 *  ArcApiErrorでない場合は、デフォルトのErrorを返す
 *  @param data APIのレスポンスBody
 *  @param defaultTitle Errorのmessage
 */
export const convertApiErrorResponseToErrorDescription = (
  data: unknown,
  defaultTitle: string
): Error => {
  const defaultError = createDefaultError(defaultTitle);
  if (!isArcApiError(data)) return defaultError;
  return convertArcApiErrorToError(data);
};

/** デフォルトのErrorを作成する
 * @param message Errorのmessage
 */
export const createDefaultError = (message: string): Error => {
  const error = new Error(message);
  error.name = '不明なエラーが発生しました';
  return error;
};

/** 引数がArcApiErrorであるかどうかを判定する
 */
export const isArcApiError = (error: unknown): error is ArcApiError => {
  if (typeof error !== 'object' || error === null) return false;
  const { code, message } = error as ArcApiError;
  return typeof code === 'string' && typeof message === 'string';
};

/** ArcApiErrorのcodeからエラーの詳細文を作成する
 * 該当するエラーが存在しない場合は不明なエラーが発生しましたを返す
 * @param code ArcApiErrorのcode
 * @returns エラーの詳細文
 */
export const findErrorCodeDescription = (
  code: ErrorCodeValue | string
): ErrorDescription | null => {
  const errorCodeKey: ErrorCodeKey =
    (Object.keys(ERROR_CODES) as ErrorCodeKey[]).find(
      (key) => ERROR_CODES[key] === code
    ) ?? 'UNEXPECTED_ERROR';
  const description = ERROR_DESCRIPTIONS[errorCodeKey];
  if (description == null) return null;
  return description;
};

/** ArcApiErrorをErrorに変換する
 * @param error ArcApiError
 * @returns Error name: code, message: message
 * @example { code: 'order_not_found', message: '注文が見つかりませんでした' } => Error name: 'order_not_found', message: '注文が見つかりませんでした'
 */
const convertArcApiErrorToError = (error: ArcApiError): Error => {
  const arcError = new Error(error.message);
  arcError.name = error.code;
  return arcError;
};
